import React, { useEffect, useState } from "react";
import { useLocation } from "react-router-dom";

// Route allowlist for PORTRAIT reading — text-heavy pages, forms, payment
// flows and the admin backoffice can be used vertically on mobile without
// needing the desktop layout. The guard only kicks in on routes that
// genuinely require landscape (the roulette-driven gameplay pages).
const PORTRAIT_ALLOWED_PREFIXES = [
  "/pagar/",              // Payment link page (screenshot from Feb 2026 report)
  "/inscrever-escola",    // Inscription form
  "/regulamento",         // Rules text
  "/reset-password",      // Password reset form
  "/diploma-individual",  // Diploma request form
  "/diplomas-finalistas", // Finalist diplomas request
  "/resultados",          // Results tables (scrollable)
  "/admin",               // Entire admin backoffice
  "/login",               // Any login screens
];

/**
 * OrientationGuard — global mobile PORTRAIT blocker.
 *
 * The app must render in LANDSCAPE on mobile devices so the desktop layout
 * (viewport meta = width=1440) can be scaled to fit. When the phone is held
 * in PORTRAIT we cover the app with an overlay asking the user to rotate.
 *
 * IMPORTANT: this component MUST detect the device's PHYSICAL orientation +
 * PHYSICAL screen size, NOT the layout-viewport (which is always 1440 wide
 * thanks to the fixed viewport meta). Using CSS media queries with `max-width`
 * won't work — we read `window.screen.width` / `screen.height` and the
 * orientation media query directly.
 */
export default function OrientationGuard() {
  const [shouldBlock, setShouldBlock] = useState(false);
  const { pathname } = useLocation();

  // If the current route is portrait-friendly, don't even mount the check
  // logic — the guard is a no-op there.
  const routeAllowsPortrait = PORTRAIT_ALLOWED_PREFIXES.some(
    (p) => pathname === p || pathname.startsWith(p),
  );

  useEffect(() => {
    // Clear flags left behind by the (removed) iOS "continue anyway" option.
    try {
      sessionStorage.removeItem("orientation_prompt_dismissed");
      sessionStorage.removeItem("forceLandscapeLayout");
      delete window.__forceLandscapeLayout;
    } catch (e) { /* no-op */ }
    // Detecting landscape on mobile is surprisingly finicky. Different
    // browsers update different signals at different points during rotation:
    //   • iOS Safari: `window.matchMedia("(orientation:portrait)").matches`
    //     can be stale until the next repaint; `window.orientation` (deg)
    //     updates immediately.
    //   • Android Chrome: `screen.orientation.type` updates before matchMedia
    //     fires, but only if the caller reads it lazily on `resize`.
    //   • All browsers: `window.innerWidth > window.innerHeight` is the
    //     ultimate ground truth because it reflects the layout viewport
    //     AFTER the browser has actually laid out the page.
    // → We combine ALL of them and treat "landscape" as *any* signal saying so.

    const orientationMq = window.matchMedia("(orientation: portrait)");

    function isCurrentlyPortrait() {
      // (1) viewport-size check — most reliable on both iOS/Android.
      const iw = window.innerWidth || document.documentElement.clientWidth || 0;
      const ih = window.innerHeight || document.documentElement.clientHeight || 0;
      if (iw > 0 && ih > 0) {
        // Layout viewport is the ground truth (same rule as index.html).
        if (iw > ih) return false;
        if (ih > iw) return true;
      }
      // (2) modern Screen Orientation API.
      if (window.screen && window.screen.orientation && typeof window.screen.orientation.type === "string") {
        const t = window.screen.orientation.type;
        if (t.startsWith("landscape")) return false;
        if (t.startsWith("portrait")) return true;
      }
      // (3) legacy iOS `window.orientation` (deg: 0/180 portrait, ±90 landscape).
      if (typeof window.orientation === "number") {
        const a = Math.abs(window.orientation);
        if (a === 90) return false;
        if (a === 0 || a === 180) return true;
      }
      // (4) fallback to matchMedia.
      return orientationMq.matches;
    }

    function check() {
      const sw = window.screen && window.screen.width ? window.screen.width : document.documentElement.clientWidth;
      const sh = window.screen && window.screen.height ? window.screen.height : document.documentElement.clientHeight;
      // Physical short side never changes with rotation on any browser, so
      // using it as the "is mobile device" gate is stable.
      const shortSide = Math.min(sw, sh);
      const isMobileDevice = shortSide <= 900;
      const portrait = isCurrentlyPortrait();
      setShouldBlock(isMobileDevice && portrait);
    }
    check();
    // Fire on orientation media-query flip AND on window resize (covers the
    // case where the browser reflows before dispatching orientationchange).
    const mqHandler = () => check();
    if (orientationMq.addEventListener) orientationMq.addEventListener("change", mqHandler);
    else orientationMq.addListener(mqHandler);
    window.addEventListener("resize", check);
    window.addEventListener("orientationchange", check);
    // Safari iOS sometimes needs several async ticks AFTER the
    // `orientationchange` event before innerWidth/innerHeight reflect the new
    // dimensions. Re-check on a short polling window so the overlay
    // disappears within ~500ms of finishing the rotation gesture.
    const rotateHandler = () => {
      [50, 150, 300, 500, 900].forEach((ms) => setTimeout(check, ms));
    };
    window.addEventListener("orientationchange", rotateHandler);
    // Some Android browsers fire `visualViewport.resize` instead of `resize`
    // when the URL bar collapses on rotation — hook into that too.
    if (window.visualViewport) {
      window.visualViewport.addEventListener("resize", check);
    }
    return () => {
      if (orientationMq.removeEventListener) orientationMq.removeEventListener("change", mqHandler);
      else orientationMq.removeListener(mqHandler);
      window.removeEventListener("resize", check);
      window.removeEventListener("orientationchange", check);
      window.removeEventListener("orientationchange", rotateHandler);
      if (window.visualViewport) window.visualViewport.removeEventListener("resize", check);
    };
  }, []);

  if (!shouldBlock || routeAllowsPortrait) return null;

  return (
    <div
      data-testid="rotate-to-landscape-prompt"
      className="fixed inset-0 z-[10000] flex flex-col items-center justify-center px-6"
      style={{ minHeight: "100vh", width: "100vw", background: "transparent", pointerEvents: "auto" }}
      aria-label="Rotate device to landscape"
      role="alert"
      onClick={(e) => e.stopPropagation()}
      onTouchStart={(e) => e.stopPropagation()}
    >
      {/* Universal icons — no text so the message is understood in any
       * language. Left icon: phone in portrait (crossed out). Middle:
       * animated rotate arrow. Right: phone in landscape (checked).
       * Icons wrapped in a semi-transparent pill so they remain readable
       * against ANY underlying page background while the outer overlay
       * itself stays fully transparent (see parent div). */}
      <div
        className="flex items-center gap-6 rounded-3xl px-6 py-5"
        style={{ background: "rgba(10,14,42,0.65)", backdropFilter: "blur(8px)", WebkitBackdropFilter: "blur(8px)", boxShadow: "0 12px 40px rgba(0,0,0,0.45)" }}
        aria-hidden
      >
        <svg width="72" height="120" viewBox="0 0 72 120" xmlns="http://www.w3.org/2000/svg">
          <rect x="6" y="6" width="60" height="108" rx="10" stroke="#f87171" strokeWidth="3" fill="none" />
          <rect x="12" y="14" width="48" height="90" rx="2" fill="#7f1d1d" fillOpacity="0.4" />
          <circle cx="36" cy="110" r="2.5" fill="#f87171" />
          <line x1="14" y1="14" x2="58" y2="106" stroke="#f87171" strokeWidth="4" strokeLinecap="round" />
        </svg>
        <svg width="80" height="80" viewBox="0 0 80 80" xmlns="http://www.w3.org/2000/svg" className="rotate-arrow-anim">
          <path d="M 12 40 A 28 28 0 1 1 68 40" stroke="#facc15" strokeWidth="5" strokeLinecap="round" fill="none" />
          <path d="M 62 30 L 72 40 L 62 50 Z" fill="#facc15" />
        </svg>
        <svg width="120" height="72" viewBox="0 0 120 72" xmlns="http://www.w3.org/2000/svg">
          <rect x="6" y="6" width="108" height="60" rx="10" stroke="#4ade80" strokeWidth="3" fill="none" />
          <rect x="14" y="12" width="90" height="48" rx="2" fill="#14532d" fillOpacity="0.4" />
          <circle cx="110" cy="36" r="2.5" fill="#4ade80" />
          <path d="M 46 36 L 56 46 L 76 26" stroke="#4ade80" strokeWidth="5" strokeLinecap="round" strokeLinejoin="round" fill="none" />
        </svg>
      </div>

    </div>
  );
}
